import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface RiskRow {
  readonly riskId: string;
  readonly riskCode: string;
  readonly riskTitle: string;
  readonly riskCategory: string;
  readonly riskLevel: string;
  readonly status: string;
}

const CATEGORIES = [
  '', 'STRATEGIC', 'OPERATIONAL', 'FINANCIAL', 'COMPLIANCE', 'CYBER',
  'DATA_PRIVACY', 'THIRD_PARTY', 'REPUTATIONAL', 'TECHNOLOGY', 'LEGAL',
];
const LEVELS = ['', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const STATUSES = ['', 'IDENTIFIED', 'ASSESSED', 'MITIGATING', 'MONITORED', 'CLOSED', 'ACCEPTED'];

/**
 * GR L30.05 — Risk Register (`/security/grc/risks`). The GRC risk ledger backed
 * by `security_risk`: enterprise risks with their category, computed level and
 * treatment status. Filterable by category, level and status.
 */
export function GrRisksPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<RiskRow[]>([]);
  const [riskCategory, setRiskCategory] = useState('');
  const [riskLevel, setRiskLevel] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (riskCategory) params.set('riskCategory', riskCategory);
    if (riskLevel) params.set('riskLevel', riskLevel);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: RiskRow[] }>(`/v1/security/risks${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, riskCategory, riskLevel, status]);

  return (
    <main aria-label="security-risks">
      <h1>{t('gr30.risks.title')}</h1>
      <div className="filters">
        <label>
          {t('gr30.risks.labelCategory')}
          <select aria-label="filter-category" value={riskCategory} onChange={(e) => setRiskCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || t('gr30.risks.all')}</option>)}
          </select>
        </label>
        <label>
          {t('gr30.risks.labelLevel')}
          <select aria-label="filter-level" value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)}>
            {LEVELS.map((l) => <option key={l || 'all'} value={l}>{l || t('gr30.risks.all')}</option>)}
          </select>
        </label>
        <label>
          {t('gr30.risks.labelStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr30.risks.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr30.risks.loading')}</p> : (
        <>
          <p aria-label="risk-count">{t('gr30.risks.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr30.risks.empty')}</p> : (
            <table aria-label="risk-table">
              <thead><tr><th>{t('gr30.risks.colCode')}</th><th>{t('gr30.risks.colTitle')}</th><th>{t('gr30.risks.colCategory')}</th><th>{t('gr30.risks.colLevel')}</th><th>{t('gr30.risks.colStatus')}</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.riskId}>
                    <td>{r.riskCode}</td><td>{r.riskTitle}</td><td>{r.riskCategory}</td>
                    <td>{r.riskLevel}</td><td>{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
